import { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Palette, Plus, Trash2, Image as ImageIcon, Sparkles, MessageSquare, LayoutGrid, Contact, ScrollText, Paintbrush, Star, Quote, Tag } from "lucide-react";
import { RichTextEditor } from "./RichTextEditor";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "./ui/select";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { translations } from "../i18n/translations";
import { TRIAL_DAYS, SUPPORT_WHATSAPP } from "../lib/constants";
import { LANG_KEYS, LANG_LABELS, FEATURE_ICON_NAMES, CONTACT_TYPES, ICON_MAP, contactPreset, DEFAULT_CONTACT_HEADING, DEFAULT_CONTACT_SUB, DEFAULT_CARDS, DEFAULT_FOOTER_TAGLINE, DEFAULT_SOCIAL, DEFAULT_FOOTER_COLS, DEFAULT_PAGES } from "../lib/landingConfig";

const ml = (fn) => Object.fromEntries(LANG_KEYS.map((l) => [l, fn(l)]));
const DEFAULT_ICONS = ["BellRing", "FileCheck2", "Boxes", "BarChart3", "Users", "PenLine"];
const PLAN_META = [
  { id: "starter", price: "99", original: "149", popular: false },
  { id: "business", price: "199", original: "299", popular: true },
  { id: "professional", price: "299", original: "449", popular: false },
  { id: "enterprise", price: "", original: "", popular: false },
];

const buildSeed = () => {
  const g = (l, path) => path.split(".").reduce((o, k) => (o == null ? o : o[k]), translations[l]?.landing);
  const itemsLen = (translations.ar?.landing?.features?.items || []).length;
  return {
    theme: { primary: "#2563eb", heroFrom: "#1e3a8a", heroTo: "#0f172a" },
    hero: {
      badge: ml((l) => String(g(l, "hero.badge") || "").replace("{days}", TRIAL_DAYS)),
      title: ml((l) => g(l, "hero.title") || ""),
      subtitle: ml((l) => g(l, "hero.subtitle") || ""),
      startTrial: ml((l) => g(l, "hero.startTrial") || ""),
      whatsapp: ml((l) => g(l, "hero.whatsapp") || ""),
    },
    features: {
      heading: ml((l) => g(l, "features.heading") || ""),
      sub: ml((l) => g(l, "features.sub") || ""),
      items: Array.from({ length: itemsLen }).map((_, i) => ({
        icon: DEFAULT_ICONS[i] || "Star",
        title: ml((l) => g(l, `features.items.${i}.title`) || ""),
        desc: ml((l) => g(l, `features.items.${i}.desc`) || ""),
      })),
    },
    contacts: [],
    footer: ml((l) => String(g(l, "footer") || "").replace("{year}", new Date().getFullYear()).replace("{wa}", SUPPORT_WHATSAPP)),
    social: {
      heading: { ar: "موثوق من عملائنا", en: "Trusted by our clients", ur: "ہمارے کلائنٹس کا اعتماد" },
      sub: { ar: "", en: "", ur: "" },
      testimonials: [],
      partners: [],
    },
    plans: PLAN_META.map((m) => ({
      id: m.id,
      name: ml((l) => translations[l]?.landing?.plans?.[m.id]?.name || ""),
      tagline: ml((l) => translations[l]?.landing?.plans?.[m.id]?.tagline || ""),
      price: m.price, original: m.original, popular: m.popular,
      features: (translations.ar?.landing?.plans?.[m.id]?.features || []).map(
        (_, i) => ml((l) => (translations[l]?.landing?.plans?.[m.id]?.features || [])[i] || "")),
      missing: (translations.ar?.landing?.plans?.[m.id]?.missing || []).map(
        (_, i) => ml((l) => (translations[l]?.landing?.plans?.[m.id]?.missing || [])[i] || "")),
    })),
    terms: { title: { ar: "الشروط والأحكام", en: "Terms & Conditions", ur: "شرائط و ضوابط" }, body: { ar: "", en: "", ur: "" } },
    privacy: { title: { ar: "سياسة الخصوصية", en: "Privacy Policy", ur: "پرائیویسی پالیسی" }, body: { ar: "", en: "", ur: "" } },
    contactHeading: DEFAULT_CONTACT_HEADING,
    contactSub: DEFAULT_CONTACT_SUB,
    cards: DEFAULT_CARDS.map((c) => ({ ...c, extra: [...(c.extra || [])] })),
    footerTagline: DEFAULT_FOOTER_TAGLINE,
    socialLinks: { ...DEFAULT_SOCIAL },
    footerCols: DEFAULT_FOOTER_COLS.map((c) => ({ h: c.h, links: c.links.map((l) => ({ ...l })) })),
    pages: DEFAULT_PAGES.map((p) => ({ ...p })),
  };
};

const mergeInit = (stored) => {
  const s = buildSeed();
  const st = stored || {};
  return {
    theme: { ...s.theme, ...(st.theme || {}) },
    hero: { ...s.hero, ...(st.hero || {}) },
    features: {
      heading: st.features?.heading || s.features.heading,
      sub: st.features?.sub || s.features.sub,
      items: Array.isArray(st.features?.items) && st.features.items.length ? st.features.items : s.features.items,
    },
    contacts: Array.isArray(st.contacts) ? st.contacts : s.contacts,
    footer: st.footer || s.footer,
    social: {
      heading: st.social?.heading || s.social.heading,
      sub: st.social?.sub || s.social.sub,
      testimonials: Array.isArray(st.social?.testimonials) ? st.social.testimonials : [],
      partners: Array.isArray(st.social?.partners) ? st.social.partners : [],
    },
    plans: Array.isArray(st.plans) && st.plans.length ? st.plans : s.plans,
    terms: { ...s.terms, ...(st.terms || {}) },
    privacy: { ...s.privacy, ...(st.privacy || {}) },
    contactHeading: st.contactHeading || s.contactHeading,
    contactSub: st.contactSub || s.contactSub,
    cards: Array.isArray(st.cards) && st.cards.length ? st.cards : s.cards,
    footerTagline: st.footerTagline || s.footerTagline,
    socialLinks: { ...s.socialLinks, ...(st.socialLinks || {}) },
    footerCols: Array.isArray(st.footerCols) && st.footerCols.length ? st.footerCols : s.footerCols,
    pages: Array.isArray(st.pages) && st.pages.length ? st.pages : s.pages,
  };
};

const ImgUploadBtn = ({ fileId, onUploaded, label = "رفع صورة", round = false, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const r = await api.uploadPartnerLogo(f); onUploaded(r.logo_id); toast.success("تم رفع الصورة"); }
    catch (er) { toast.error(er?.response?.data?.detail || "تعذّر الرفع"); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-2">
      <div className={`h-11 w-11 border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden ${round ? "rounded-full" : "rounded-lg"}`}>
        {fileId ? <img src={fileUrl(fileId)} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-4 w-4 text-slate-300" />}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={pick} data-testid={testid} />
      <Button variant="outline" size="sm" type="button" onClick={() => ref.current?.click()} disabled={busy} className="gap-1.5 text-xs">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />} {label}
      </Button>
    </div>
  );
};

export const LandingEditorDialog = ({ open, onOpenChange, embedded = false }) => {
  const { platform, refreshPlatform } = useAuth();
  const [cfg, setCfg] = useState(() => mergeInit(platform?.landing));
  const [name, setName] = useState(platform?.name || "WorkZone");
  const [tagline, setTagline] = useState(platform?.tagline || "");
  const [logoId, setLogoId] = useState(platform?.logo_id || null);
  const [lng, setLng] = useState("ar");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // language sub-switcher
  const LangTabs = () => (
    <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1" data-testid="landing-lang-tabs">
      {LANG_KEYS.map((l) => (
        <button key={l} type="button" data-testid={`landing-lang-${l}`} onClick={() => setLng(l)}
          className={`px-3 py-1 text-xs rounded-md transition ${lng === l ? "bg-white shadow-sm font-semibold text-slate-800" : "text-slate-500"}`}>
          {LANG_LABELS[l]}
        </button>
      ))}
    </div>
  );

  // helpers to update multilang leaf at a section
  const setHero = (k, v) => setCfg((c) => ({ ...c, hero: { ...c.hero, [k]: { ...c.hero[k], [lng]: v } } }));
  const setFeat = (k, v) => setCfg((c) => ({ ...c, features: { ...c.features, [k]: { ...c.features[k], [lng]: v } } }));
  const setFooter = (v) => setCfg((c) => ({ ...c, footer: { ...c.footer, [lng]: v } }));
  const setTheme = (k, v) => setCfg((c) => ({ ...c, theme: { ...c.theme, [k]: v } }));
  const setLegal = (sec, k, v) => setCfg((c) => ({ ...c, [sec]: { ...c[sec], [k]: { ...(c[sec]?.[k] || {}), [lng]: v } } }));

  const setItem = (i, k, v, isText = true) => setCfg((c) => {
    const items = c.features.items.map((it, idx) => idx === i ? { ...it, [k]: isText ? { ...(it[k] || {}), [lng]: v } : v } : it);
    return { ...c, features: { ...c.features, items } };
  });
  const addItem = () => setCfg((c) => ({ ...c, features: { ...c.features, items: [...c.features.items, { icon: "Star", title: {}, desc: {} }] } }));
  const delItem = (i) => setCfg((c) => ({ ...c, features: { ...c.features, items: c.features.items.filter((_, idx) => idx !== i) } }));

  const setContact = (i, k, v, isText = false) => setCfg((c) => {
    const contacts = c.contacts.map((ct, idx) => idx === i ? { ...ct, [k]: isText ? { ...(ct[k] || {}), [lng]: v } : v } : ct);
    return { ...c, contacts };
  });
  const addContact = () => setCfg((c) => ({ ...c, contacts: [...c.contacts, { type: "whatsapp", value: "", label: {} }] }));
  const delContact = (i) => setCfg((c) => ({ ...c, contacts: c.contacts.filter((_, idx) => idx !== i) }));

  // social: testimonials + partners
  const setSocial = (k, v) => setCfg((c) => ({ ...c, social: { ...c.social, [k]: { ...c.social[k], [lng]: v } } }));
  const setTm = (i, k, v, isText = false) => setCfg((c) => {
    const testimonials = c.social.testimonials.map((tm, idx) => idx === i ? { ...tm, [k]: isText ? { ...(tm[k] || {}), [lng]: v } : v } : tm);
    return { ...c, social: { ...c.social, testimonials } };
  });
  const addTm = () => setCfg((c) => ({ ...c, social: { ...c.social, testimonials: [...c.social.testimonials, { name: "", role: "", quote: {}, rating: 5, avatar_id: null }] } }));
  const delTm = (i) => setCfg((c) => ({ ...c, social: { ...c.social, testimonials: c.social.testimonials.filter((_, idx) => idx !== i) } }));
  const setPartner = (i, k, v) => setCfg((c) => {
    const partners = c.social.partners.map((p, idx) => idx === i ? { ...p, [k]: v } : p);
    return { ...c, social: { ...c.social, partners } };
  });
  const addPartner = () => setCfg((c) => ({ ...c, social: { ...c.social, partners: [...c.social.partners, { logo_id: null, name: "" }] } }));
  const delPartner = (i) => setCfg((c) => ({ ...c, social: { ...c.social, partners: c.social.partners.filter((_, idx) => idx !== i) } }));

  // plans (pricing) CRUD
  const setPlan = (i, k, v) => setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const setPlanML = (i, k, v) => setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => idx === i ? { ...p, [k]: { ...(p[k] || {}), [lng]: v } } : p) }));
  const setPlanListItem = (i, key, j, v) => setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => idx === i ? { ...p, [key]: (p[key] || []).map((it, jj) => jj === j ? { ...(it || {}), [lng]: v } : it) } : p) }));
  const addPlanListItem = (i, key) => setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => idx === i ? { ...p, [key]: [...(p[key] || []), {}] } : p) }));
  const delPlanListItem = (i, key, j) => setCfg((c) => ({ ...c, plans: c.plans.map((p, idx) => idx === i ? { ...p, [key]: (p[key] || []).filter((_, jj) => jj !== j) } : p) }));
  const addPlan = () => setCfg((c) => ({ ...c, plans: [...c.plans, { id: "plan_" + Date.now(), name: {}, tagline: {}, price: "", original: "", popular: false, features: [], missing: [] }] }));
  const delPlan = (i) => setCfg((c) => ({ ...c, plans: c.plans.filter((_, idx) => idx !== i) }));

  // contact cards (top contact section)
  const setCard = (i, k, v) => setCfg((c) => ({ ...c, cards: c.cards.map((cd, idx) => idx === i ? { ...cd, [k]: v } : cd) }));
  const setCardExtra = (i, v) => setCfg((c) => ({ ...c, cards: c.cards.map((cd, idx) => idx === i ? { ...cd, extra: v.split("\n").map((x) => x.trim()).filter(Boolean) } : cd) }));
  const addCard = () => setCfg((c) => ({ ...c, cards: [...c.cards, { cat: "", title: "", email: "", extra: [], action: "تواصل معنا" }] }));
  const delCard = (i) => setCfg((c) => ({ ...c, cards: c.cards.filter((_, idx) => idx !== i) }));
  const setSocialLink = (k, v) => setCfg((c) => ({ ...c, socialLinks: { ...(c.socialLinks || {}), [k]: v } }));

  // footer columns + links
  const setColH = (i, v) => setCfg((c) => ({ ...c, footerCols: c.footerCols.map((col, idx) => idx === i ? { ...col, h: v } : col) }));
  const addCol = () => setCfg((c) => ({ ...c, footerCols: [...c.footerCols, { h: "عمود جديد", links: [] }] }));
  const delCol = (i) => setCfg((c) => ({ ...c, footerCols: c.footerCols.filter((_, idx) => idx !== i) }));
  const setLink = (ci, li, k, v) => setCfg((c) => ({ ...c, footerCols: c.footerCols.map((col, idx) => idx === ci ? { ...col, links: col.links.map((l, j) => j === li ? { ...l, [k]: v } : l) } : col) }));
  const addLink = (ci) => setCfg((c) => ({ ...c, footerCols: c.footerCols.map((col, idx) => idx === ci ? { ...col, links: [...(col.links || []), { label: "رابط جديد", slug: "" }] } : col) }));
  const delLink = (ci, li) => setCfg((c) => ({ ...c, footerCols: c.footerCols.map((col, idx) => idx === ci ? { ...col, links: col.links.filter((_, j) => j !== li) } : col) }));

  // info pages
  const setPage = (i, k, v) => setCfg((c) => ({ ...c, pages: c.pages.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const addPage = () => setCfg((c) => ({ ...c, pages: [...c.pages, { slug: "page-" + Date.now(), title: "صفحة جديدة", body: "" }] }));
  const delPage = (i) => setCfg((c) => ({ ...c, pages: c.pages.filter((_, idx) => idx !== i) }));

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { const r = await api.uploadPlatformLogo(f); setLogoId(r.logo_id); toast.success("تم رفع الشعار"); }
    catch (er) { toast.error(er?.response?.data?.detail || "تعذّر رفع الشعار"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.updatePlatform({ name: name.trim(), tagline: tagline.trim() });
      await api.updateLanding(cfg);
      await refreshPlatform();
      toast.success("تم حفظ تصميم الواجهة");
      onOpenChange?.(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };

  const isRtlLng = lng === "ar" || lng === "ur";
  const preview = useMemo(() => cfg.theme, [cfg.theme]);

  const inner = (
    <>
        <div className="flex items-center justify-between gap-2 sticky top-0 bg-white/95 backdrop-blur py-2 z-10 border-b border-slate-100">
          <span className="text-xs text-slate-500">لغة التحرير:</span>
          <LangTabs />
        </div>

        <Tabs defaultValue="brand" dir="rtl" className="mt-2">
          <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100">
            <TabsTrigger value="brand" data-testid="tab-brand" className="gap-1 text-xs"><ImageIcon className="h-3.5 w-3.5" /> الهوية</TabsTrigger>
            <TabsTrigger value="hero" data-testid="tab-hero" className="gap-1 text-xs"><Sparkles className="h-3.5 w-3.5" /> الهيرو</TabsTrigger>
            <TabsTrigger value="features" data-testid="tab-features" className="gap-1 text-xs"><LayoutGrid className="h-3.5 w-3.5" /> المميزات</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts" className="gap-1 text-xs"><Contact className="h-3.5 w-3.5" /> التواصل</TabsTrigger>
            <TabsTrigger value="social" data-testid="tab-social" className="gap-1 text-xs"><Quote className="h-3.5 w-3.5" /> العملاء</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="gap-1 text-xs"><Tag className="h-3.5 w-3.5" /> الباقات</TabsTrigger>
            <TabsTrigger value="legal" data-testid="tab-legal" className="gap-1 text-xs"><ScrollText className="h-3.5 w-3.5" /> الشروط</TabsTrigger>
            <TabsTrigger value="theme" data-testid="tab-theme" className="gap-1 text-xs"><Palette className="h-3.5 w-3.5" /> الألوان</TabsTrigger>
            <TabsTrigger value="footer" data-testid="tab-footer" className="gap-1 text-xs"><MessageSquare className="h-3.5 w-3.5" /> الفوتر</TabsTrigger>
            <TabsTrigger value="pages" data-testid="tab-pages" className="gap-1 text-xs"><ScrollText className="h-3.5 w-3.5" /> الصفحات والروابط</TabsTrigger>
          </TabsList>

          {/* BRAND */}
          <TabsContent value="brand" className="space-y-4 pt-4">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-xl border border-slate-200 bg-slate-50 grid place-items-center overflow-hidden">
                {logoId ? <img src={fileUrl(logoId)} alt="logo" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-slate-300" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={onLogo} data-testid="landing-logo-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="landing-logo-btn" className="gap-1.5">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} رفع الشعار (PNG/JPG/WEBP)
                </Button>
                <p className="text-[11px] text-slate-400 mt-1">يظهر في الهيدر وشاشة الدخول.</p>
              </div>
            </div>
            <label className="block"><span className="text-xs text-slate-500">اسم المنصة</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="landing-brand-name" className="mt-1" /></label>
            <label className="block"><span className="text-xs text-slate-500">الوصف المختصر (Tagline)</span>
              <Input value={tagline} onChange={(e) => setTagline(e.target.value)} data-testid="landing-brand-tagline" className="mt-1" /></label>
          </TabsContent>

          {/* HERO */}
          <TabsContent value="hero" className="space-y-3 pt-4" dir={isRtlLng ? "rtl" : "ltr"}>
            <label className="block"><span className="text-xs text-slate-500">نص الشارة (التجربة المجانية)</span>
              <Input value={cfg.hero.badge?.[lng] || ""} onChange={(e) => setHero("badge", e.target.value)} data-testid="hero-badge" className="mt-1" /></label>
            <label className="block"><span className="text-xs text-slate-500">العنوان الرئيسي</span>
              <Input value={cfg.hero.title?.[lng] || ""} onChange={(e) => setHero("title", e.target.value)} data-testid="hero-title" className="mt-1" /></label>
            <label className="block"><span className="text-xs text-slate-500">النص الوصفي</span>
              <Textarea rows={4} value={cfg.hero.subtitle?.[lng] || ""} onChange={(e) => setHero("subtitle", e.target.value)} data-testid="hero-subtitle" className="mt-1" /></label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block"><span className="text-xs text-slate-500">زر البدء</span>
                <Input value={cfg.hero.startTrial?.[lng] || ""} onChange={(e) => setHero("startTrial", e.target.value)} data-testid="hero-start" className="mt-1" /></label>
              <label className="block"><span className="text-xs text-slate-500">زر واتساب</span>
                <Input value={cfg.hero.whatsapp?.[lng] || ""} onChange={(e) => setHero("whatsapp", e.target.value)} data-testid="hero-whatsapp" className="mt-1" /></label>
            </div>
          </TabsContent>

          {/* FEATURES */}
          <TabsContent value="features" className="space-y-3 pt-4" dir={isRtlLng ? "rtl" : "ltr"}>
            <label className="block"><span className="text-xs text-slate-500">عنوان القسم</span>
              <Input value={cfg.features.heading?.[lng] || ""} onChange={(e) => setFeat("heading", e.target.value)} data-testid="feat-heading" className="mt-1" /></label>
            <label className="block"><span className="text-xs text-slate-500">وصف القسم</span>
              <Input value={cfg.features.sub?.[lng] || ""} onChange={(e) => setFeat("sub", e.target.value)} data-testid="feat-sub" className="mt-1" /></label>
            <div className="space-y-3">
              {cfg.features.items.map((it, i) => {
                const Icon = ICON_MAP[it.icon] || ICON_MAP.Star;
                return (
                  <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`feat-item-${i}`}>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-lg grid place-items-center shrink-0" style={{ backgroundColor: `${preview.primary}1a`, color: preview.primary }}><Icon className="h-4 w-4" /></div>
                      <Select value={it.icon} onValueChange={(v) => setItem(i, "icon", v, false)}>
                        <SelectTrigger className="h-8 w-40 text-xs" data-testid={`feat-icon-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent position="item-aligned">
                          {FEATURE_ICON_NAMES.map((n) => <SelectItem key={n} value={n} className="text-xs">{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button onClick={() => delItem(i)} data-testid={`feat-del-${i}`} className="ms-auto text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <Input placeholder="العنوان" value={it.title?.[lng] || ""} onChange={(e) => setItem(i, "title", e.target.value)} data-testid={`feat-title-${i}`} className="h-8 text-sm" />
                    <Textarea rows={2} placeholder="الوصف" value={it.desc?.[lng] || ""} onChange={(e) => setItem(i, "desc", e.target.value)} data-testid={`feat-desc-${i}`} className="text-sm" />
                  </div>
                );
              })}
              <Button variant="outline" size="sm" onClick={addItem} data-testid="feat-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة ميزة</Button>
            </div>
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts" className="space-y-3 pt-4">
            <p className="text-xs text-slate-400">تظهر كأزرار في أسفل الصفحة. اختر النوع وأدخل الرقم/الرابط.</p>
            {cfg.contacts.map((ct, i) => {
              const preset = contactPreset(ct.type);
              return (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`contact-${i}`}>
                  <div className="flex items-center gap-2">
                    <Select value={ct.type} onValueChange={(v) => setContact(i, "type", v)}>
                      <SelectTrigger className="h-8 w-40 text-xs" data-testid={`contact-type-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent position="item-aligned">
                        {CONTACT_TYPES.map((c) => <SelectItem key={c.type} value={c.type} className="text-xs">{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <button onClick={() => delContact(i)} data-testid={`contact-del-${i}`} className="ms-auto text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Input dir="ltr" placeholder={preset.type === "whatsapp" || preset.type === "phone" ? "9665xxxxxxxx" : preset.type === "email" ? "info@example.com" : "https://..."}
                    value={ct.value || ""} onChange={(e) => setContact(i, "value", e.target.value)} data-testid={`contact-value-${i}`} className="h-8 text-sm" />
                  <Input placeholder={`التسمية الظاهرة (${preset.label})`} value={ct.label?.[lng] || ""} onChange={(e) => setContact(i, "label", e.target.value, true)} data-testid={`contact-label-${i}`} className="h-8 text-sm" />
                </div>
              );
            })}
            <Button variant="outline" size="sm" onClick={addContact} data-testid="contact-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة قناة تواصل</Button>
          </TabsContent>

          {/* SOCIAL — testimonials & partners */}
          <TabsContent value="social" className="space-y-4 pt-4">
            <div dir={isRtlLng ? "rtl" : "ltr"} className="space-y-2">
              <label className="block"><span className="text-xs text-slate-500">عنوان القسم</span>
                <Input value={cfg.social.heading?.[lng] || ""} onChange={(e) => setSocial("heading", e.target.value)} data-testid="social-heading" className="mt-1" /></label>
              <label className="block"><span className="text-xs text-slate-500">وصف القسم (اختياري)</span>
                <Input value={cfg.social.sub?.[lng] || ""} onChange={(e) => setSocial("sub", e.target.value)} data-testid="social-sub" className="mt-1" /></label>
            </div>

            <div className="space-y-2">
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><Quote className="h-4 w-4 text-blue-600" /> آراء العملاء</p>
              {cfg.social.testimonials.map((tm, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`tm-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <ImgUploadBtn fileId={tm.avatar_id} round label="صورة" testid={`tm-avatar-${i}`} onUploaded={(id) => setTm(i, "avatar_id", id)} />
                    <button onClick={() => delTm(i)} data-testid={`tm-del-${i}`} className="ms-auto text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="الاسم" value={tm.name || ""} onChange={(e) => setTm(i, "name", e.target.value)} data-testid={`tm-name-${i}`} className="h-8 text-sm" />
                    <Input placeholder="المسمى / الشركة" value={tm.role || ""} onChange={(e) => setTm(i, "role", e.target.value)} data-testid={`tm-role-${i}`} className="h-8 text-sm" />
                  </div>
                  <Textarea rows={2} dir={isRtlLng ? "rtl" : "ltr"} placeholder="نص الرأي" value={tm.quote?.[lng] || ""} onChange={(e) => setTm(i, "quote", e.target.value, true)} data-testid={`tm-quote-${i}`} className="text-sm" />
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">التقييم:</span>
                    <Select value={String(tm.rating || 5)} onValueChange={(v) => setTm(i, "rating", Number(v))}>
                      <SelectTrigger className="h-8 w-24 text-xs" data-testid={`tm-rating-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent position="item-aligned">
                        {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)} className="text-xs">{n} ★</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addTm} data-testid="tm-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة رأي عميل</Button>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><ImageIcon className="h-4 w-4 text-blue-600" /> شعارات الشركاء / العملاء</p>
              {cfg.social.partners.map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 flex items-center gap-2" data-testid={`partner-${i}`}>
                  <ImgUploadBtn fileId={p.logo_id} label="شعار" testid={`partner-logo-${i}`} onUploaded={(id) => setPartner(i, "logo_id", id)} />
                  <Input placeholder="اسم الشريك (اختياري)" value={p.name || ""} onChange={(e) => setPartner(i, "name", e.target.value)} data-testid={`partner-name-${i}`} className="h-8 text-sm flex-1" />
                  <button onClick={() => delPartner(i)} data-testid={`partner-del-${i}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPartner} data-testid="partner-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة شعار شريك</Button>
            </div>
          </TabsContent>

          {/* PLANS — pricing & features */}
          <TabsContent value="plans" className="space-y-3 pt-4">
            <p className="text-xs text-slate-400">عدّل أسماء الباقات وأسعارها وقوائم المميزات (المتوفرة ✓ وغير المتوفرة ✗). السعر بالريال شهريًا، واترك السعر فارغًا لباقة «حسب الطلب».</p>
            {cfg.plans.map((p, i) => (
              <div key={p.id} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`plan-${p.id}`}>
                <div className="flex items-center gap-2 flex-wrap" dir={isRtlLng ? "rtl" : "ltr"}>
                  <Input placeholder="اسم الباقة" value={p.name?.[lng] || ""} onChange={(e) => setPlanML(i, "name", e.target.value)} data-testid={`plan-name-${p.id}`} className="h-8 text-sm flex-1 min-w-[120px] font-semibold" />
                  <button onClick={() => delPlan(i)} data-testid={`plan-del-${p.id}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                </div>
                <Input placeholder="وصف مختصر" dir={isRtlLng ? "rtl" : "ltr"} value={p.tagline?.[lng] || ""} onChange={(e) => setPlanML(i, "tagline", e.target.value)} data-testid={`plan-tagline-${p.id}`} className="h-8 text-sm" />
                <div className="grid grid-cols-3 gap-2">
                  <label className="text-[11px] text-slate-500">السعر
                    <Input dir="ltr" value={p.price || ""} onChange={(e) => setPlan(i, "price", e.target.value)} data-testid={`plan-price-${p.id}`} className="h-8 text-sm mt-0.5 font-mono-nums" placeholder="حسب الطلب" /></label>
                  <label className="text-[11px] text-slate-500">السعر قبل الخصم
                    <Input dir="ltr" value={p.original || ""} onChange={(e) => setPlan(i, "original", e.target.value)} data-testid={`plan-original-${p.id}`} className="h-8 text-sm mt-0.5 font-mono-nums" /></label>
                  <label className="text-[11px] text-slate-500 flex flex-col">الأكثر طلبًا
                    <button type="button" onClick={() => setPlan(i, "popular", !p.popular)} data-testid={`plan-popular-${p.id}`}
                      className={`h-8 mt-0.5 rounded-md text-xs font-semibold transition ${p.popular ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                      {p.popular ? "مميّزة ★" : "عادية"}
                    </button></label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-emerald-700">المميزات المتوفرة ✓</p>
                    {(p.features || []).map((f, j) => (
                      <div key={j} className="flex items-center gap-1.5">
                        <Input dir={isRtlLng ? "rtl" : "ltr"} value={f?.[lng] || ""} onChange={(e) => setPlanListItem(i, "features", j, e.target.value)} data-testid={`plan-feat-${p.id}-${j}`} className="h-7 text-xs" />
                        <button onClick={() => delPlanListItem(i, "features", j)} data-testid={`plan-feat-del-${p.id}-${j}`} className="text-rose-400 hover:bg-rose-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addPlanListItem(i, "features")} data-testid={`plan-feat-add-${p.id}`} className="h-7 gap-1 text-[11px]"><Plus className="h-3 w-3" /> إضافة ميزة</Button>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-slate-400">غير متوفّرة ✗</p>
                    {(p.missing || []).map((f, j) => (
                      <div key={j} className="flex items-center gap-1.5">
                        <Input dir={isRtlLng ? "rtl" : "ltr"} value={f?.[lng] || ""} onChange={(e) => setPlanListItem(i, "missing", j, e.target.value)} data-testid={`plan-miss-${p.id}-${j}`} className="h-7 text-xs" />
                        <button onClick={() => delPlanListItem(i, "missing", j)} data-testid={`plan-miss-del-${p.id}-${j}`} className="text-rose-400 hover:bg-rose-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={() => addPlanListItem(i, "missing")} data-testid={`plan-miss-add-${p.id}`} className="h-7 gap-1 text-[11px]"><Plus className="h-3 w-3" /> إضافة عنصر</Button>
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addPlan} data-testid="plan-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة باقة</Button>
          </TabsContent>

          {/* LEGAL */}
          <TabsContent value="legal" className="space-y-4 pt-4" dir={isRtlLng ? "rtl" : "ltr"}>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="font-semibold text-sm text-slate-700">الشروط والأحكام</p>
              <Input placeholder="عنوان" value={cfg.terms?.title?.[lng] || ""} onChange={(e) => setLegal("terms", "title", e.target.value)} data-testid="terms-title" className="h-8 text-sm" />
              <Textarea rows={6} placeholder="اكتب نص الشروط والأحكام هنا…" value={cfg.terms?.body?.[lng] || ""} onChange={(e) => setLegal("terms", "body", e.target.value)} data-testid="terms-body" className="text-sm" />
            </div>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="font-semibold text-sm text-slate-700">سياسة الخصوصية</p>
              <Input placeholder="عنوان" value={cfg.privacy?.title?.[lng] || ""} onChange={(e) => setLegal("privacy", "title", e.target.value)} data-testid="privacy-title" className="h-8 text-sm" />
              <Textarea rows={6} placeholder="اكتب نص سياسة الخصوصية هنا…" value={cfg.privacy?.body?.[lng] || ""} onChange={(e) => setLegal("privacy", "body", e.target.value)} data-testid="privacy-body" className="text-sm" />
            </div>
            <p className="text-[11px] text-slate-400">اترك الحقل فارغًا لإخفاء الرابط من الفوتر.</p>
          </TabsContent>

          {/* THEME */}
          <TabsContent value="theme" className="space-y-4 pt-4">
            {[["primary", "اللون الأساسي (الأزرار)"], ["heroFrom", "بداية خلفية الهيرو"], ["heroTo", "نهاية خلفية الهيرو"]].map(([k, lbl]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-600">{lbl}</span>
                <div className="flex items-center gap-2">
                  <Input dir="ltr" value={cfg.theme[k]} onChange={(e) => setTheme(k, e.target.value)} data-testid={`theme-${k}-text`} className="h-9 w-28 text-sm font-mono-nums" />
                  <input type="color" value={cfg.theme[k]} onChange={(e) => setTheme(k, e.target.value)} data-testid={`theme-${k}`} className="h-9 w-12 rounded border border-slate-200 cursor-pointer" />
                </div>
              </div>
            ))}
            <div className="rounded-xl overflow-hidden border border-slate-200" data-testid="theme-preview">
              <div className="p-6 text-white" style={{ backgroundImage: `linear-gradient(to left, ${preview.heroFrom}, ${preview.heroTo})` }}>
                <p className="font-heading font-bold text-lg">معاينة الهيرو</p>
                <button className="mt-3 rounded-md px-4 py-2 text-sm font-semibold text-white" style={{ backgroundColor: preview.primary }}>زر رئيسي</button>
              </div>
            </div>
          </TabsContent>

          {/* FOOTER */}
          <TabsContent value="footer" className="space-y-3 pt-4" dir={isRtlLng ? "rtl" : "ltr"}>
            <label className="block"><span className="text-xs text-slate-500">نص حقوق الفوتر</span>
              <Textarea rows={3} value={cfg.footer?.[lng] || ""} onChange={(e) => setFooter(e.target.value)} data-testid="footer-text" className="mt-1 text-sm" /></label>
            <label className="block"><span className="text-xs text-slate-500">وصف الشركة تحت الشعار</span>
              <Textarea rows={2} value={cfg.footerTagline || ""} onChange={(e) => setCfg((c) => ({ ...c, footerTagline: e.target.value }))} data-testid="footer-tagline" className="mt-1 text-sm" /></label>
            <div className="rounded-xl border border-slate-200 p-3 space-y-2">
              <p className="font-semibold text-sm text-slate-700">روابط التواصل الاجتماعي</p>
              {["linkedin", "twitter", "facebook", "youtube"].map((k) => (
                <label key={k} className="block"><span className="text-xs text-slate-500 capitalize">{k}</span>
                  <Input dir="ltr" placeholder="https://..." value={cfg.socialLinks?.[k] || ""} onChange={(e) => setSocialLink(k, e.target.value)} data-testid={`social-link-${k}`} className="h-8 text-sm mt-0.5" /></label>
              ))}
            </div>
          </TabsContent>

          {/* PAGES & FOOTER LINKS */}
          <TabsContent value="pages" className="space-y-4 pt-4" dir="rtl">
            <div className="space-y-2">
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><Contact className="h-4 w-4 text-blue-600" /> بطاقات التواصل (أعلى قسم تواصل)</p>
              <label className="block"><span className="text-xs text-slate-500">عنوان القسم</span>
                <Input value={cfg.contactHeading || ""} onChange={(e) => setCfg((c) => ({ ...c, contactHeading: e.target.value }))} data-testid="contact-heading-edit" className="h-8 text-sm mt-0.5" /></label>
              <label className="block"><span className="text-xs text-slate-500">وصف القسم</span>
                <Input value={cfg.contactSub || ""} onChange={(e) => setCfg((c) => ({ ...c, contactSub: e.target.value }))} data-testid="contact-sub-edit" className="h-8 text-sm mt-0.5" /></label>
              {(cfg.cards || []).map((cd, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`card-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="التصنيف" value={cd.cat || ""} onChange={(e) => setCard(i, "cat", e.target.value)} data-testid={`card-cat-${i}`} className="h-8 text-sm" />
                    <button onClick={() => delCard(i)} data-testid={`card-del-${i}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <Input placeholder="العنوان" value={cd.title || ""} onChange={(e) => setCard(i, "title", e.target.value)} data-testid={`card-title-${i}`} className="h-8 text-sm" />
                  <Input dir="ltr" placeholder="البريد الإلكتروني" value={cd.email || ""} onChange={(e) => setCard(i, "email", e.target.value)} data-testid={`card-email-${i}`} className="h-8 text-sm" />
                  <Textarea rows={2} placeholder="أسطر إضافية (أرقام/مدن) — سطر لكل عنصر" value={(cd.extra || []).join("\n")} onChange={(e) => setCardExtra(i, e.target.value)} data-testid={`card-extra-${i}`} className="text-sm" />
                  <Input placeholder="نص الزر" value={cd.action || ""} onChange={(e) => setCard(i, "action", e.target.value)} data-testid={`card-action-${i}`} className="h-8 text-sm" />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addCard} data-testid="card-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة بطاقة</Button>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><LayoutGrid className="h-4 w-4 text-blue-600" /> أعمدة الفوتر وروابطها</p>
              <p className="text-[11px] text-slate-400">اربط كل رابط بصفحة تعريفية عبر «معرّف الصفحة» (slug) المطابق لصفحة في قسم الصفحات أدناه.</p>
              {(cfg.footerCols || []).map((col, ci) => (
                <div key={ci} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`col-${ci}`}>
                  <div className="flex items-center gap-2">
                    <Input placeholder="عنوان العمود" value={col.h || ""} onChange={(e) => setColH(ci, e.target.value)} data-testid={`col-h-${ci}`} className="h-8 text-sm font-semibold" />
                    <button onClick={() => delCol(ci)} data-testid={`col-del-${ci}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  {(col.links || []).map((l, li) => (
                    <div key={li} className="flex items-center gap-1.5">
                      <Input placeholder="نص الرابط" value={l.label || ""} onChange={(e) => setLink(ci, li, "label", e.target.value)} data-testid={`link-label-${ci}-${li}`} className="h-7 text-xs flex-1" />
                      <Input dir="ltr" placeholder="slug" value={l.slug || ""} onChange={(e) => setLink(ci, li, "slug", e.target.value)} data-testid={`link-slug-${ci}-${li}`} className="h-7 text-xs w-28" />
                      <button onClick={() => delLink(ci, li)} data-testid={`link-del-${ci}-${li}`} className="text-rose-400 hover:bg-rose-50 rounded p-1"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => addLink(ci)} data-testid={`link-add-${ci}`} className="h-7 gap-1 text-[11px]"><Plus className="h-3 w-3" /> إضافة رابط</Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addCol} data-testid="col-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة عمود</Button>
            </div>

            <div className="space-y-2 border-t border-slate-100 pt-4">
              <p className="font-semibold text-sm text-slate-700 flex items-center gap-1.5"><ScrollText className="h-4 w-4 text-blue-600" /> الصفحات التعريفية</p>
              <p className="text-[11px] text-slate-400">كل صفحة لها معرّف (slug) يُربط من روابط الفوتر، وتُفتح على الرابط /page/&lt;slug&gt;.</p>
              {(cfg.pages || []).map((p, i) => (
                <div key={i} className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`page-${i}`}>
                  <div className="flex items-center gap-2">
                    <Input dir="ltr" placeholder="slug" value={p.slug || ""} onChange={(e) => setPage(i, "slug", e.target.value)} data-testid={`page-slug-${i}`} className="h-8 text-xs w-32" />
                    <Input placeholder="عنوان الصفحة" value={p.title || ""} onChange={(e) => setPage(i, "title", e.target.value)} data-testid={`page-title-${i}`} className="h-8 text-sm flex-1 font-semibold" />
                    <button onClick={() => delPage(i)} data-testid={`page-del-${i}`} className="text-rose-500 hover:bg-rose-50 rounded p-1.5"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <span className="text-[11px] text-slate-400">محتوى الصفحة (تنسيق غني: عناوين، نقاط، روابط، صور)</span>
                  <RichTextEditor value={p.body || ""} onChange={(html) => setPage(i, "body", html)} testid={`page-body-${i}`} />
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addPage} data-testid="page-add" className="gap-1.5"><Plus className="h-4 w-4" /> إضافة صفحة</Button>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2 mt-2">
          {!embedded && <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy} data-testid="landing-cancel">إلغاء</Button>}
          <Button onClick={save} disabled={busy} data-testid="landing-save" className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} حفظ ونشر
          </Button>
        </DialogFooter>
    </>
  );

  if (embedded) return <div className="font-body" dir="rtl" data-testid="landing-editor-body">{inner}</div>;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl font-body max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="landing-editor-dialog">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl flex items-center gap-2"><Paintbrush className="h-5 w-5 text-blue-600" /> تصميم الواجهة الرئيسية</DialogTitle>
          <DialogDescription className="text-start">خصّص الهيدر والمميزات وقنوات التواصل والشروط والألوان لكل اللغات. يظهر للزوار مباشرة بعد الحفظ.</DialogDescription>
        </DialogHeader>
        {inner}
      </DialogContent>
    </Dialog>
  );
};
